import React from 'react';
import { Link } from 'react-router-dom';
import { HandHeart, Calendar, Newspaper, ChevronRight, Sparkles, Building2, Info } from 'lucide-react';
import { PageHeader } from './MassTimes';
import EditableText from '../components/EditableText';

const sections = [
  {
    title: "I'm New",
    contentKey: 'parish_life_im_new_desc',
    defaultDesc: "Just arrived, exploring the Catholic Faith, or coming back after time away? Start here — gentle first steps and a warm welcome.",
    label: "Parish Life: I'm New card",
    icon: Sparkles,
    path: '/parish-life/im-new',
  },
  {
    title: 'About',
    contentKey: 'parish_life_about_desc',
    defaultDesc: "Learn about our parish story, clergy and staff, and the shared mission of St. Mary's and St. Joseph's here on Vancouver Island.",
    label: 'Parish Life: About card',
    icon: Info,
    path: '/about',
  },
  {
    title: 'Ministries & Programs',
    contentKey: 'parish_life_ministries_desc',
    defaultDesc: "Discover the many ways you can grow in faith and serve — liturgical, community, faith formation, and outreach ministries at both St. Mary's and St. Joseph's.",
    label: 'Parish Life: Ministries & Programs card',
    icon: HandHeart,
    path: '/parish-life/ministries',
  },
  {
    title: 'Parish Calendar',
    contentKey: 'parish_life_calendar_desc',
    defaultDesc: "Masses, sacraments, meetings, socials, and special liturgies. See what's coming up across both worship communities.",
    label: 'Parish Life: Parish Calendar card',
    icon: Calendar,
    path: '/parish-life/calendar',
  },
  {
    title: 'News & Resources',
    contentKey: 'parish_life_news_desc',
    defaultDesc: 'The latest parish news together with helpful Catholic resources — the Archdiocese of Vancouver, daily readings, the Vatican, and more.',
    label: 'Parish Life: News & Resources card',
    icon: Newspaper,
    path: '/parish-life/news',
  },
  {
    title: 'Hall Rental',
    contentKey: 'parish_life_hall_rental_desc',
    defaultDesc: 'Book our parish hall for family celebrations, community events, and meetings. See features, rental rates, rules, and submit a request online.',
    label: 'Parish Life: Hall Rental card',
    icon: Building2,
    path: '/parish-life/hall-rental',
  },
];

const ParishLife = () => {
  return (
    <>
      <PageHeader
        eyebrow="Get Involved"
        title="Parish Life"
        subtitle="Every gift and every calling has a place in our parish. Explore ministries, upcoming events, news, and resources."
      />

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map(s => {
            const Icon = s.icon;
            return (
              <div key={s.path} className="bg-white border border-[#e9e0cc] hover:border-[#3a2359] p-8 flex flex-col group">
                <Link to={s.path} className="w-14 h-14 rounded-full bg-[#f2ead6] flex items-center justify-center text-[#3a2359] mb-5 group-hover:bg-[#3a2359] group-hover:text-[#e6d4a8]">
                  <Icon size={22} strokeWidth={1.6} />
                </Link>
                <Link to={s.path}>
                  <h2 className="font-display text-3xl text-[#2a1a45] mb-3 leading-tight hover:text-[#3a2359]">{s.title}</h2>
                </Link>
                <div className="flex-1">
                  <EditableText
                    contentKey={s.contentKey}
                    label={s.label}
                    rows={4}
                    defaultValue={s.defaultDesc}
                    paragraphClass="text-sm text-[#5a534a] leading-relaxed"
                  />
                </div>
                <Link to={s.path} className="mt-6 inline-flex items-center text-sm font-medium text-[#3a2359] hover:gap-2 gap-1 self-start">
                  Explore <ChevronRight size={16} />
                </Link>
              </div>
            );
          })}
        </div>

        <div className="mt-16 bg-[#f2ead6]/60 border border-[#e9e0cc] p-10 text-center">
          <h3 className="font-display text-3xl text-[#2a1a45] mb-3">Ready to Get Involved?</h3>
          <p className="text-[#5a534a] max-w-xl mx-auto mb-6">
            Speak with a member of our parish staff after Mass, or contact the parish office. We would love to help you find where God is calling you.
          </p>
          <Link to="/contact" data-testid="parish-life-contact-btn" className="inline-block px-6 py-3 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75]">Contact the Parish Office</Link>
        </div>
      </section>
    </>
  );
};

export default ParishLife;
